import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sparkles, Crown } from 'lucide-react';

import { useQuoteEntitlement } from '@/hooks/useQuoteEntitlement';

/**
 * QuotaBadge — small pill shown at the top of Quote Builder screens.
 * Displays remaining quota for the current month and (Free plan only)
 * a "Pro'ya Yükselt" call-to-action.
 */
export const QuotaBadge = () => {
  const { t } = useTranslation('quotes');
  const { entitlement, isPro, loading } = useQuoteEntitlement();
  if (loading || !entitlement) return null;

  const usage = entitlement.usage || {};
  const limits = entitlement.limits || {};
  const proUntil = entitlement.expires_at ? String(entitlement.expires_at).slice(0, 10) : null;

  return (
    <div
      className={`inline-flex items-center gap-3 rounded-xl px-3 py-1.5 text-xs font-medium ${
        isPro
          ? 'bg-amber-50 border border-amber-200 text-amber-800'
          : 'bg-slate-50 border border-slate-200 text-slate-700'
      }`}
      data-testid="quota-badge"
    >
      {isPro ? <Crown className="w-3.5 h-3.5" /> : <Sparkles className="w-3.5 h-3.5" />}
      <span data-testid="quota-plan-label">
        {entitlement.plan === 'business' ? 'Business' : isPro ? t('quotaBadge.planPro') : t('quotaBadge.planFree')}
      </span>
      <span className="text-[#94A3B8]">•</span>
      <span data-testid="quota-quote-count">
        {t('quotaBadge.quotes')}: <b>{usage.quote_count ?? 0}</b>/{limits.quote_count ?? t('upgrade.unlimited')}
      </span>
      <span className="text-[#94A3B8]">•</span>
      <span data-testid="quota-ai-count">
        {t('quotaBadge.ai')}: <b>{usage.ai_count ?? 0}</b>/{limits.ai_count ?? 0}
      </span>
      {!isPro && <span>{t('upgrade.customers')}: <b>{usage.customer_count ?? 0}</b>/{limits.customer_limit ?? 20}</span>}
      {isPro && proUntil && (
        <>
          <span className="text-[#94A3B8]">•</span>
          <span data-testid="quota-expires">{t('quotaBadge.expires', { date: proUntil })}</span>
        </>
      )}
      {!isPro && (
        <>
          <span className="text-[#94A3B8]">•</span>
          <Link
            to="/quote-upgrade"
            className="text-[#2563EB] hover:underline font-semibold"
            data-testid="quota-upgrade-link"
          >
            {t('quotaBadge.upgrade')}
          </Link>
        </>
      )}
    </div>
  );
};

export default QuotaBadge;
